import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import classnames from "classnames";
import { LEVERAGE_STEPS } from "../../TradeForm/constants";
import "./LeveragePicker.sass";

const TICKS_PER_STEP = 12;
const CENTER_TICK = Math.floor(TICKS_PER_STEP / 2);
const SCROLL_END_DELAY = 120;
// Vertical mouse wheel distance for one step (a notch is ~100px)
const WHEEL_STEP_DELTA = 50;
const WHEEL_LINE_HEIGHT = 16;
// Mouse drags shorter than this are clicks on a step
const DRAG_CLICK_THRESHOLD = 5;

const getStepWidth = (scroller) =>
  scroller.firstElementChild?.getBoundingClientRect().width || 1;

const clampIndex = (index, count) => Math.min(count - 1, Math.max(0, index));

// Step under the center of the wheel for its current scroll position
const getScrolledIndex = (scroller, count) =>
  clampIndex(Math.round(scroller.scrollLeft / getStepWidth(scroller)), count);

const nearestStepIndex = (steps, value) => {
  const numeric = Number(value);
  let bestIndex = 0;

  steps.forEach((step, index) => {
    if (Math.abs(step - numeric) < Math.abs(steps[bestIndex] - numeric)) {
      bestIndex = index;
    }
  });

  return bestIndex;
};

const formatLeverage = (step) => `${step}x`;

/**
 * Ticks before the first step and after the last one are hidden, the center
 * tick of each step is major, and active under the selected step.
 */
const getTickModifier = (tick, index, activeIndex, count) => {
  const isOutsideScale =
    (index === 0 && tick < CENTER_TICK) ||
    (index === count - 1 && tick > CENTER_TICK);

  if (isOutsideScale) return "mobile-trade__tick--hidden";
  if (tick !== CENTER_TICK) return null;

  return index === activeIndex
    ? "mobile-trade__tick--active"
    : "mobile-trade__tick--major";
};

/**
 * Horizontally swipeable wheel snapping to steps (leverage by default).
 *
 * @param {object} props
 * @param {number} props.value
 * @param {(value: number) => void} props.onChange
 * @param {number[]} [props.steps]
 * @param {(step: number) => string} [props.formatStep]
 */
export const LeveragePicker = ({
  value,
  onChange,
  steps = LEVERAGE_STEPS,
  formatStep = formatLeverage,
}) => {
  const scrollerRef = useRef(null);
  const scrollEndTimerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(() =>
    nearestStepIndex(steps, value),
  );
  // Step the wheel heads to, ahead of activeIndex while it scrolls smoothly
  const targetIndexRef = useRef(activeIndex);
  const wheelDeltaRef = useRef(0);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) return;

    scroller.scrollLeft = activeIndex * getStepWidth(scroller);
    // Scroll to the initial step only once, user swipes drive it afterwards
  }, []);

  useEffect(() => {
    const step = steps[activeIndex];

    if (step !== value) onChange(step);
    // Snap a custom desktop leverage (e.g. 37x) to the nearest step on open
  }, []);

  useEffect(() => () => clearTimeout(scrollEndTimerRef.current), []);

  const handleScroll = () => {
    const index = getScrolledIndex(scrollerRef.current, steps.length);

    setActiveIndex(index);

    if (!dragRef.current) targetIndexRef.current = index;

    clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      if (steps[index] !== value) onChange(steps[index]);
    }, SCROLL_END_DELAY);
  };

  const scrollToStep = (index) => {
    const scroller = scrollerRef.current;

    targetIndexRef.current = index;
    scroller.scrollTo({
      left: index * getStepWidth(scroller),
      behavior: "smooth",
    });
  };

  // A mouse wheel only scrolls vertically, turn it into steps; horizontal
  // trackpad swipes keep the native scroll. Non-passive to stop the page scroll
  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) return undefined;

    const handleWheel = (event) => {
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

      event.preventDefault();

      const lineHeight = event.deltaMode === 1 ? WHEEL_LINE_HEIGHT : 1;

      wheelDeltaRef.current += event.deltaY * lineHeight;

      if (Math.abs(wheelDeltaRef.current) < WHEEL_STEP_DELTA) return;

      const direction = Math.sign(wheelDeltaRef.current);
      const next = clampIndex(targetIndexRef.current + direction, steps.length);

      wheelDeltaRef.current = 0;
      if (next !== targetIndexRef.current) scrollToStep(next);
    };

    scroller.addEventListener("wheel", handleWheel, { passive: false });

    return () => scroller.removeEventListener("wheel", handleWheel);
  }, [steps.length]);

  // Drag with a mouse, touch already scrolls natively
  const handlePointerDown = (event) => {
    suppressClickRef.current = false;

    if (event.pointerType !== "mouse" || event.button !== 0) return;

    dragRef.current = {
      startX: event.clientX,
      startScroll: scrollerRef.current.scrollLeft,
      pointerId: event.pointerId,
      hasMoved: false,
    };
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;

    if (!drag) return;

    const dx = event.clientX - drag.startX;

    if (!drag.hasMoved && Math.abs(dx) < DRAG_CLICK_THRESHOLD) return;

    if (!drag.hasMoved) {
      drag.hasMoved = true;
      scrollerRef.current.setPointerCapture(drag.pointerId);
      setIsDragging(true);
    }

    scrollerRef.current.scrollLeft = drag.startScroll - dx;
  };

  const handlePointerUp = () => {
    const drag = dragRef.current;

    dragRef.current = null;

    if (!drag?.hasMoved) return;

    suppressClickRef.current = true;
    setIsDragging(false);
    scrollToStep(getScrolledIndex(scrollerRef.current, steps.length));
  };

  // The click that ends a drag shouldn't jump to the step under the cursor
  const handleClickCapture = (event) => {
    if (!suppressClickRef.current) return;

    suppressClickRef.current = false;
    event.stopPropagation();
  };

  return (
    <div className="mobile-trade__leverage-picker">
      <div
        ref={scrollerRef}
        className={classnames("mobile-trade__leverage-scroller", {
          "mobile-trade__leverage-scroller--dragging": isDragging,
        })}
        onScroll={handleScroll}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClickCapture={handleClickCapture}
      >
        {steps.map((step, index) => (
          <button
            key={step}
            type="button"
            className="mobile-trade__leverage-step"
            onClick={() => scrollToStep(index)}
            aria-pressed={index === activeIndex}
          >
            <span className="mobile-trade__leverage-value">
              {formatStep(step)}
            </span>

            <span className="mobile-trade__ticks">
              {Array.from({ length: TICKS_PER_STEP }, (_, tick) => (
                <span
                  key={tick}
                  className={classnames(
                    "mobile-trade__tick",
                    getTickModifier(tick, index, activeIndex, steps.length),
                  )}
                />
              ))}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
